import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { energyService } from '../../services/energyService';
import { PILLAR_COLORS, toPoints } from '../../utils/energyPalette';
import { categoryLabel } from './energyLabels';
import { logger } from '../../utils/logger';

// Level 2 — one pillar's categories. Rows are the drill-down into Level 3.
const PillarBreakdown = ({ pillar, range, onSelectCategory }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Last-ISSUED-wins: a slow response for an older range must never overwrite
    // a newer one (the #294 lesson).
    const reqRef = useRef(0);

    useEffect(() => {
        const ticket = ++reqRef.current;
        let alive = true;
        setLoading(true);
        setError(null);
        energyService
            .getPillarBreakdown(pillar, range || {})
            .then((res) => {
                if (!alive || ticket !== reqRef.current) return;
                setData(res || null);
                setLoading(false);
            })
            .catch((err) => {
                if (!alive || ticket !== reqRef.current) return;
                logger.warn('ENERGY', `pillar breakdown failed: ${err?.message || err}`);
                setError(err);
                setLoading(false);
            });
        return () => { alive = false; };
    }, [pillar, range]);

    const color = PILLAR_COLORS[pillar] || '#9CA3AF';
    const categories = data?.categories || [];
    const total = categories.reduce((acc, c) => acc + Math.max(0, toPoints(c?.pts)), 0);

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-4 text-[13px] text-pai-meta">
                <Loader2 size={14} className="animate-spin" />
                {t('energyLoading', 'Loading…')}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-[13px] text-pai-red break-words" data-testid="energy-level2-error">
                {t('energyError', "Couldn't load your energy data.")}
            </div>
        );
    }

    return (
        <section data-testid="energy-level2" className="min-w-0 max-w-full">
            <header className="flex items-center justify-between gap-2 mb-2.5 min-w-0">
                <span className="pai-eyebrow truncate min-w-0">
                    {t('energyCategories', 'Categories')}
                </span>
                <span
                    data-testid="energy-level2-total"
                    className="text-[13.5px] font-bold text-white tnum flex-shrink-0"
                >
                    {data?.pts ?? '0.00'}
                </span>
            </header>

            {categories.length === 0 ? (
                <p data-testid="energy-level2-empty" className="text-[13px] text-pai-meta break-words">
                    {t('energyNoCategories', 'No activity in this pillar for the selected range.')}
                </p>
            ) : (
                <ul className="space-y-1.5 min-w-0">
                    {categories.map((c) => {
                        const value = Math.max(0, toPoints(c?.pts));
                        const width = total > 0 ? (value / total) * 100 : 0;
                        return (
                            <li key={c.category} className="min-w-0">
                                <button
                                    type="button"
                                    data-testid={`energy-cat-${c.category}`}
                                    onClick={() => onSelectCategory?.(c.category)}
                                    className="group w-full text-left px-3 py-2.5 rounded-row border border-pai-subtle bg-white/[0.025] hover:bg-white/[0.07] hover:border-pai transition-colors min-w-0 max-w-full"
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        <span className="text-[14px] font-medium text-pai-body truncate min-w-0 flex-1">
                                            {categoryLabel(t, c.category)}
                                        </span>
                                        {/* Verbatim 2dp string. */}
                                        <span className="text-[13.5px] font-bold text-white tnum flex-shrink-0">
                                            {c?.pts ?? '0.00'}
                                        </span>
                                        <ChevronRight size={14} className="text-pai-meta flex-shrink-0" />
                                    </span>
                                    {/* The burn bar. The pillar's hue is what the
                                        drill-down is ABOUT, so the fill keeps it;
                                        the track is the design's neutral
                                        white/0.14, brightening with the row. */}
                                    <span className="mt-2 block h-1.5 w-full rounded-[2px] bg-white/[0.14] overflow-hidden">
                                        <span
                                            className="block h-full rounded-[2px] transition-opacity opacity-80 group-hover:opacity-100"
                                            style={{ width: `${width}%`, background: color }}
                                        />
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

export default PillarBreakdown;
